import { useEffect, useState, useRef } from "react";
import { FiMoreVertical } from "react-icons/fi";
import {
  IoChevronDownCircleOutline,
  IoChevronUpCircleOutline,
} from "react-icons/io5";
import { RxCross2 } from "react-icons/rx";
import TaskDetailModal from "./TaskDetailModal";

// Global variable for copied task (outside component for persistence)
let copiedTask = null;

// Utility function to renumber all tasks and their subtasks
const renumberTasks = (tasks) => {
  return tasks.map((task, index) => {
    const newSerialNumber = index + 1;
    const updatedTask = {
      ...task,
      serialNumber: newSerialNumber,
      currentIndex: index,
    };
    
    // Renumber subtasks if they exist
    if (task.subTasks && task.subTasks.length > 0) {
      updatedTask.subTasks = task.subTasks.map((subTask, subIndex) => ({
        ...subTask,
        serialNumber: `${newSerialNumber}.${subIndex + 1}`,
        currentIndex: subIndex,
      }));
    }
    
    return updatedTask;
  });
};

export default function TaskRow({
  task,
  index,
  setData,
  expandedIndex,
  setExpandedIndex,
  isDraggingOver,
  onDragStart,
  onDragEnter,
  onDragOver,
  onDragLeave,
  onDragEnd,
  searchQuery = "",
}) {
  // State for tracking subtasks visibility
  const [showSubtasks, setShowSubtasks] = useState(expandedIndex === index);
  // State for tracking popover menu visibility
  const [showPopover, setShowPopover] = useState(false);
  const [popoverSearch, setPopoverSearch] = useState("");
  // State for new popovers
  // const [showPredecessorPopover, setShowPredecessorPopover] = useState(false);
  // const [showSuccessorPopover, setShowSuccessorPopover] = useState(false);
  // const [showDepartmentPopover, setShowDepartmentPopover] = useState(false);
  // const [showAssignedToPopover, setShowAssignedToPopover] = useState(false);
  const [predecessorPopovers, setPredecessorPopovers] = useState({});
  const [successorPopovers, setSuccessorPopovers] = useState({});
  const [departmentPopovers, setDepartmentPopovers] = useState({});
  const [assignedToPopovers, setAssignedToPopovers] = useState({});
  // State for search inputs
  const [predecessorSearch, setPredecessorSearch] = useState("");
  const [successorSearch, setSuccessorSearch] = useState("");
  const [departmentSearch, setDepartmentSearch] = useState("");
  const [assignedToSearch, setAssignedToSearch] = useState("");
  // Local state for the task data (to enable modifications)
  const [taskData, setTaskData] = useState(task);
  // State for editing Category (main task)
  const [isEditingCategory, setIsEditingCategory] = useState(false);
  const [categoryValue, setCategoryValue] = useState(task.category);
  // State for editing TaskName (main task)
  const [isEditingTaskName, setIsEditingTaskName] = useState(false);
  const [taskNameValue, setTaskNameValue] = useState(task.taskName);
  // State for editing Progress (main task)
  const [isEditingProgress, setIsEditingProgress] = useState(false);
  const [progressValue, setProgressValue] = useState(task.progress);
  // State for editing subtask fields
  const [editingSubtaskFields, setEditingSubtaskFields] = useState({});
  const [subtaskValues, setSubtaskValues] = useState(
    (task.subTasks || []).map((subTask) => ({
      category: subTask.category,
      taskName: subTask.taskName,
      progress: subTask.progress,
    }))
  );
  // State for date picker visibility (main task)
  const [showStartDatePicker, setShowStartDatePicker] = useState(false);
  const [showEndDatePicker, setShowEndDatePicker] = useState(false);
  // State for subtask date picker visibility
  const [subtaskDatePickers, setSubtaskDatePickers] = useState({});

  const [draggedSubIndex, setDraggedSubIndex] = useState(null);

  // State for task detail modal
  const [showTaskModal, setShowTaskModal] = useState(false);

  // Refs for auto-blur timeout (main task)
  const categoryTimeoutRef = useRef(null);
  const taskNameTimeoutRef = useRef(null);
  const progressTimeoutRef = useRef(null);
  // Refs for input elements (main task)
  const categoryInputRef = useRef(null);
  const taskNameInputRef = useRef(null);
  const progressInputRef = useRef(null);
  // Refs for subtask inputs and timeouts
  const subtaskInputRefs = useRef({});
  const subtaskTimeoutRefs = useRef({});
  const popoverRef = useRef(null);
  // Refs for new popovers
  // const predecessorPopoverRef = useRef(null);
  // const successorPopoverRef = useRef(null);
  // const departmentPopoverRef = useRef(null);
  // const assignedToPopoverRef = useRef(null);
  const popoverRefs = useRef({
    predecessor: {},
    successor: {},
    department: {},
    assignedTo: {},
  });
  // Refs for date inputs (main task)
  const startDateInputRef = useRef(null);
  const endDateInputRef = useRef(null);
  // Refs for subtask date inputs
  const subtaskDateInputRefs = useRef({});

  // Static options for popovers
  const actionOptions = [
    "See Details",
    "Copy",
    ...(copiedTask ? ["Paste Above", "Paste Below"] : []),
    "Delete",
    "Add Subtask",
    "Add Row Above",
    "Add Row Below",
  ];
  const predecessorOptions = ["Task-1", "Task-2", "Task-3", "Task-4"];
  const successorOptions = ["Task-5", "Task-6", "Task-7", "Task-8"];
  const departmentOptions = ["Onsite", "Remote", "Hybrid", "Field"];
  const assignedToOptions = ["Karthik", "Anita", "Vijay", "Priya"];

  // Sync showSubtasks with expandedIndex
  useEffect(() => {
    setShowSubtasks(expandedIndex === index);
  }, [expandedIndex, index]);

  // Sync taskData with task prop
  useEffect(() => {
    setTaskData(task);
    setCategoryValue(task.category);
    setTaskNameValue(task.taskName);
    setProgressValue(task.progress);
    setSubtaskValues(
      (task.subTasks || []).map((subTask) => ({
        category: subTask.category,
        taskName: subTask.taskName,
        progress: subTask.progress,
      }))
    );
  }, [task]);

  // Handle clicks outside the popovers to close them
  // useEffect(() => {
  //   const handleOutsideClick = (event) => {
  //     if (
  //       showPopover &&
  //       popoverRef.current &&
  //       !popoverRef.current.contains(event.target)
  //     ) {
  //       setShowPopover(false);
  //       setPopoverSearch("");
  //     }
  //     if (
  //       showPredecessorPopover &&
  //       predecessorPopoverRef.current &&
  //       !predecessorPopoverRef.current.contains(event.target)
  //     ) {
  //       setShowPredecessorPopover(false);
  //       setPredecessorSearch("");
  //     }
  //     if (
  //       showSuccessorPopover &&
  //       successorPopoverRef.current &&
  //       !successorPopoverRef.current.contains(event.target)
  //     ) {
  //       setShowSuccessorPopover(false);
  //       setSuccessorSearch("");
  //     }
  //     if (
  //       showDepartmentPopover &&
  //       departmentPopoverRef.current &&
  //       !departmentPopoverRef.current.contains(event.target)
  //     ) {
  //       setShowDepartmentPopover(false);
  //       setDepartmentSearch("");
  //     }
  //     if (
  //       showAssignedToPopover &&
  //       assignedToPopoverRef.current &&
  //       !assignedToPopoverRef.current.contains(event.target)
  //     ) {
  //       setShowAssignedToPopover(false);
  //       setAssignedToSearch("");
  //     }
  //   };

  //   document.addEventListener("mousedown", handleOutsideClick);
  //   return () => {
  //     document.removeEventListener("mousedown", handleOutsideClick);
  //   };
  // }, [
  //   showPopover,
  //   showPredecessorPopover,
  //   showSuccessorPopover,
  //   showDepartmentPopover,
  //   showAssignedToPopover,
  // ]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        showPopover &&
        popoverRef.current &&
        !popoverRef.current.contains(event.target)
      ) {
        setShowPopover(false);
        setPopoverSearch("");
      }
      Object.keys(predecessorPopovers).forEach((key) => {
        if (
          predecessorPopovers[key] &&
          popoverRefs.current.predecessor[key] &&
          !popoverRefs.current.predecessor[key].contains(event.target)
        ) {
          setPredecessorPopovers((prev) => ({ ...prev, [key]: false }));
          setPredecessorSearch("");
        }
      });
      Object.keys(successorPopovers).forEach((key) => {
        if (
          successorPopovers[key] &&
          popoverRefs.current.successor[key] &&
          !popoverRefs.current.successor[key].contains(event.target)
        ) {
          setSuccessorPopovers((prev) => ({ ...prev, [key]: false }));
          setSuccessorSearch("");
        }
      });
      Object.keys(departmentPopovers).forEach((key) => {
        if (
          departmentPopovers[key] &&
          popoverRefs.current.department[key] &&
          !popoverRefs.current.department[key].contains(event.target)
        ) {
          setDepartmentPopovers((prev) => ({ ...prev, [key]: false }));
          setDepartmentSearch("");
        }
      });
      Object.keys(assignedToPopovers).forEach((key) => {
        if (
          assignedToPopovers[key] &&
          popoverRefs.current.assignedTo[key] &&
          !popoverRefs.current.assignedTo[key].contains(event.target)
        ) {
          setAssignedToPopovers((prev) => ({ ...prev, [key]: false }));
          setAssignedToSearch("");
        }
      });
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [
    showPopover,
    predecessorPopovers,
    successorPopovers,
    departmentPopovers,
    assignedToPopovers,
  ]);

  // Handle auto-blur for Category input (main task)
  useEffect(() => {
    if (isEditingCategory) {
      categoryTimeoutRef.current = setTimeout(() => {
        setIsEditingCategory(false);
        updateTaskData();
      }, 7000);
    }
    return () => clearTimeout(categoryTimeoutRef.current);
  }, [isEditingCategory, categoryValue]);

  // Handle auto-blur for TaskName input (main task)
  useEffect(() => {
    if (isEditingTaskName) {
      taskNameTimeoutRef.current = setTimeout(() => {
        setIsEditingTaskName(false);
        updateTaskData();
      }, 7000);
    }
    return () => clearTimeout(taskNameTimeoutRef.current);
  }, [isEditingTaskName, taskNameValue]);

  // Handle auto-blur for Progress input (main task)
  useEffect(() => {
    if (isEditingProgress) {
      progressTimeoutRef.current = setTimeout(() => {
        setIsEditingProgress(false);
        updateTaskData();
      }, 7000);
    }
    return () => clearTimeout(progressTimeoutRef.current);
  }, [isEditingProgress, progressValue]);

  // Handle auto-blur for subtask inputs
  useEffect(() => {
    Object.keys(editingSubtaskFields).forEach((key) => {
      if (editingSubtaskFields[key]) {
        const [subIndex, field] = key.split("-");
        subtaskTimeoutRefs.current[key] = setTimeout(() => {
          setEditingSubtaskFields((prev) => ({
            ...prev,
            [key]: false,
          }));
          updateSubtaskData(parseInt(subIndex), field);
        }, 7000);
      }
    });
    return () => {
      Object.keys(subtaskTimeoutRefs.current).forEach((key) => {
        clearTimeout(subtaskTimeoutRefs.current[key]);
      });
    };
  }, [editingSubtaskFields, subtaskValues]);

  // Focus input when editing starts (main task)
  useEffect(() => {
    if (isEditingCategory && categoryInputRef.current) {
      categoryInputRef.current.focus();
      adjustTextareaHeight(categoryInputRef.current);
    }
    if (isEditingTaskName && taskNameInputRef.current) {
      taskNameInputRef.current.focus();
      adjustTextareaHeight(taskNameInputRef.current);
    }
    if (isEditingProgress && progressInputRef.current) {
      progressInputRef.current.focus();
    }
  }, [isEditingCategory, isEditingTaskName, isEditingProgress]);

  // Focus subtask input when editing starts
  useEffect(() => {
    Object.keys(editingSubtaskFields).forEach((key) => {
      if (editingSubtaskFields[key] && subtaskInputRefs.current[key]) {
        subtaskInputRefs.current[key].focus();
        if (key.includes("category") || key.includes("taskName")) {
          adjustTextareaHeight(subtaskInputRefs.current[key]);
        }
      }
    });
  }, [editingSubtaskFields]);

  // Open date picker when shown (main task)
  useEffect(() => {
    if (showStartDatePicker && startDateInputRef.current) {
      startDateInputRef.current.showPicker();
      startDateInputRef.current.focus();
    }
    if (showEndDatePicker && endDateInputRef.current) {
      endDateInputRef.current.showPicker();
      endDateInputRef.current.focus();
    }
  }, [showStartDatePicker, showEndDatePicker]);

  // Open date picker when shown (subtasks)
  useEffect(() => {
    Object.keys(subtaskDatePickers).forEach((key) => {
      if (subtaskDatePickers[key] && subtaskDateInputRefs.current[key]) {
        subtaskDateInputRefs.current[key].showPicker();
        subtaskDateInputRefs.current[key].focus();
      }
    });
  }, [subtaskDatePickers]);

  // Adjust textarea height dynamically
  const adjustTextareaHeight = (textarea) => {
    if (textarea) {
      textarea.style.height = "auto"; // Reset height
      textarea.style.height = `${textarea.scrollHeight}px`; // Set to scroll height
    }
  };

  // Update setData with new values (main task)
  const updateTaskData = () => {
    const validatedProgress = Math.max(
      0,
      Math.min(100, parseInt(progressValue) || 0)
    ).toString();
    setProgressValue(validatedProgress);
    setData((prevData) => {
      const newData = [...prevData];
      newData[index] = {
        ...taskData,
        category: categoryValue,
        taskName: taskNameValue,
        progress: validatedProgress,
      };
      return newData;
    });
  };

  // Update setData with new subtask values
  const updateSubtaskData = (subIndex, field) => {
    let value = subtaskValues[subIndex][field];
    if (field === "progress") {
      value = Math.max(0, Math.min(100, parseInt(value) || 0)).toString();
      setSubtaskValues((prev) => {
        const newValues = [...prev];
        newValues[subIndex] = { ...newValues[subIndex], progress: value };
        return newValues;
      });
    }
    setData((prevData) => {
      const newData = [...prevData];
      const updatedSubTasks = [...newData[index].subTasks];
      updatedSubTasks[subIndex] = {
        ...updatedSubTasks[subIndex],
        [field]: value,
      };
      newData[index] = {
        ...newData[index],
        subTasks: updatedSubTasks,
      };
      return newData;
    });
  };

  // Calculate duration between start and end date
  const calculateDuration = (startDate, endDate) => {
    if (
      !startDate ||
      !endDate ||
      startDate === "Yet to Start/-" ||
      endDate === "------------"
    ) {
      return "14 days";
    }

    try {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return `${diffDays} days`;
    } catch (e) {
      return "14 days";
    }
  };

  // Handle checkbox change
  const handleCheckboxChange = (isSubtask = false, subTaskIndex = null) => {
    if (isSubtask && subTaskIndex !== null) {
      const updatedSubTasks = [...taskData.subTasks];
      updatedSubTasks[subTaskIndex] = {
        ...updatedSubTasks[subTaskIndex],
        progress:
          updatedSubTasks[subTaskIndex].progress === "100" ? "0" : "100",
      };

      setTaskData({
        ...taskData,
        subTasks: updatedSubTasks,
      });
      setData((prevData) => {
        const newData = [...prevData];
        newData[index] = {
          ...newData[index],
          subTasks: updatedSubTasks,
        };
        return newData;
      });
    } else {
      setTaskData({
        ...taskData,
        progress: taskData.progress === "100" ? "0" : "100",
      });
      setData((prevData) => {
        const newData = [...prevData];
        newData[index] = {
          ...newData[index],
          progress: taskData.progress === "100" ? "0" : "100",
        };
        return newData;
      });
    }
  };

  // Add a new subtask
  const addSubtask = () => {
    const newSubtask = {
      serialNumber: `${taskData.serialNumber}.${taskData.subTasks.length + 1}`,
      category: taskData.category,
      taskName: "New Subtask",
      startDate: taskData.startDate,
      endDate: taskData.endDate,
      progress: "0",
      predecessor: [],
      successor: [],
      department: taskData.department,
      assignedTo: [],
      estimatedDelay: "0 days",
      currentIndex: taskData.subTasks.length,
    };

    const updatedSubTasks = [...taskData.subTasks, newSubtask];
    
    // Renumber all subtasks to ensure proper sequential numbering
    const renumberedSubTasks = updatedSubTasks.map((subTask, subIndex) => ({
      ...subTask,
      serialNumber: `${taskData.serialNumber}.${subIndex + 1}`,
      currentIndex: subIndex,
    }));
    
    setTaskData((prev) => ({
      ...prev,
      subTasks: renumberedSubTasks,
    }));

    setSubtaskValues((prev) => [
      ...prev,
      {
        category: newSubtask.category,
        taskName: newSubtask.taskName,
        progress: newSubtask.progress,
      },
    ]);

    setData((prevData) => {
      const newData = [...prevData];
      newData[index] = {
        ...newData[index],
        subTasks: renumberedSubTasks,
      };
      console.log('📝 Added subtask, renumbered subtasks:', renumberedSubTasks.map(s => s.serialNumber).join(', '));
      return newData;
    });

    setShowSubtasks(true);
    setExpandedIndex(index);
  };

  // Remove a subtask
  const removeSubtask = (subTaskIndex) => {
    const updatedSubTasks = taskData.subTasks.filter(
      (_, index) => index !== subTaskIndex
    );
    
    // Renumber remaining subtasks to ensure proper sequential numbering
    const renumberedSubTasks = updatedSubTasks.map((subTask, subIndex) => ({
      ...subTask,
      serialNumber: `${taskData.serialNumber}.${subIndex + 1}`,
      currentIndex: subIndex,
    }));
    
    const updatedSubtaskValues = subtaskValues.filter(
      (_, index) => index !== subTaskIndex
    );
    
    setTaskData({
      ...taskData,
      subTasks: renumberedSubTasks,
    });
    setSubtaskValues(updatedSubtaskValues);
    setData((prevData) => {
      const newData = [...prevData];
      newData[index] = {
        ...newData[index],
        subTasks: renumberedSubTasks,
      };
      console.log('🗑️ Removed subtask, renumbered remaining subtasks:', renumberedSubTasks.map(s => s.serialNumber).join(', '));
      return newData;
    });
  };

  // Delete the entire task
  const deleteTask = () => {
    setData((prevData) => {
      const newData = [...prevData];
      newData.splice(index, 1);
      
      // Renumber all tasks after deletion
      const renumberedData = renumberTasks(newData);
      console.log('🗑️ Deleted task, renumbered remaining tasks:', renumberedData.map(t => `${t.serialNumber}:${t.taskName.substring(0, 10)}`).join(', '));
      
      return renumberedData;
    });
    setShowPopover(false);
    setPopoverSearch("");
  };

  // Update main task dates
  const updateTaskDate = (field, value) => {
    setTaskData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setData((prevData) => {
      const newData = [...prevData];
      newData[index] = {
        ...newData[index],
        [field]: value,
      };
      return newData;
    });
  };

  // Update subtask dates
  const updateSubtaskDate = (subIndex, field, value) => {
    const updatedSubTasks = [...taskData.subTasks];
    updatedSubTasks[subIndex] = {
      ...updatedSubTasks[subIndex],
      [field]: value,
    };
    setTaskData({
      ...taskData,
      subTasks: updatedSubTasks,
    });
    setData((prevData) => {
      const newData = [...prevData];
      newData[index] = {
        ...newData[index],
        subTasks: updatedSubTasks,
      };
      return newData;
    });
  };

  // Update task fields for popovers
  const updateTaskField = (field, value) => {
    setTaskData((prev) => ({
      ...prev,
      [field]: Array.isArray(prev[field]) ? [value] : value,
    }));
    setData((prevData) => {
      const newData = [...prevData];
      newData[index] = {
        ...newData[index],
        [field]: Array.isArray(newData[index][field]) ? [value] : value,
      };
      return newData;
    });
  };

  // Update subtask fields for popovers
  const updateSubtaskField = (subIndex, field, value) => {
    const updatedSubTasks = [...taskData.subTasks];
    updatedSubTasks[subIndex] = {
      ...updatedSubTasks[subIndex],
      [field]: Array.isArray(updatedSubTasks[subIndex][field])
        ? [value]
        : value,
    };
    setTaskData({
      ...taskData,
      subTasks: updatedSubTasks,
    });
    setData((prevData) => {
      const newData = [...prevData];
      newData[index] = {
        ...newData[index],
        subTasks: updatedSubTasks,
      };
      return newData;
    });
  };

  // Handle action selection
  const handleAction = (action) => {
    switch (action) {
      case "See Details":
        setShowTaskModal(true);
        break;
      case "Copy":
        copiedTask = JSON.parse(JSON.stringify(taskData));
        console.log("Task copied:", copiedTask);
        break;
      case "Paste Above":
        if (copiedTask) {
          addRowAbove(copiedTask);
        }
        break;
      case "Paste Below":
        if (copiedTask) {
          addRowBelow(copiedTask);
        }
        break;
      case "Delete":
        deleteTask();
        break;
      case "Add Subtask":
        addSubtask();
        break;
      case "Add Row Above":
        addRowAbove();
        break;
      case "Add Row Below":
        addRowBelow();
        break;
      default:
        break;
    }
    setShowPopover(false);
    setPopoverSearch("");
  };

  // Add row above current task
  const addRowAbove = (taskToCopy = null) => {
    const newTask = taskToCopy || {
      serialNumber: 1, // Will be renumbered
      category: "Tap to enter",
      taskName: "Tap to enter",
      startDate: "",
      endDate: "",
      duration: "14 days",
      progress: "0",
      predecessor: [],
      successor: [],
      department: "Onsite",
      assignedTo: ["Karthik"],
      estimatedDelay: "2 days",
      subTasks: []
    };
    
    setData((prevData) => {
      const newData = [...prevData];
      newData.splice(index, 0, newTask);
      
      // Renumber all tasks after adding
      const renumberedData = renumberTasks(newData);
      console.log('📊 Added row above, renumbered tasks:', renumberedData.map(t => `${t.serialNumber}:${t.taskName.substring(0, 10)}`).join(', '));
      
      return renumberedData;
    });
  };

  // Add row below current task
  const addRowBelow = (taskToCopy = null) => {
    const newTask = taskToCopy || {
      serialNumber: 1, // Will be renumbered
      category: "Tap to enter",
      taskName: "Tap to enter",
      startDate: "",
      endDate: "",
      duration: "14 days",
      progress: "0",
      predecessor: [],
      successor: [],
      department: "Onsite",
      assignedTo: ["Karthik"],
      estimatedDelay: "2 days",
      subTasks: []
    };
    
    setData((prevData) => {
      const newData = [...prevData];
      newData.splice(index + 1, 0, newTask);
      
      // Renumber all tasks after adding
      const renumberedData = renumberTasks(newData);
      console.log('📊 Added row below, renumbered tasks:', renumberedData.map(t => `${t.serialNumber}:${t.taskName.substring(0, 10)}`).join(', '));
      
      return renumberedData;
    });
  };

  // Handle task click for modal opening
  const handleTaskClick = (e) => {
    // Avoid opening modal if clicking on interactive elements
    const targetElement = e.target;
    const targetClasses = targetElement.className || '';
    
    // Check if clicked element or any parent is an interactive element
    const isInteractiveElement = 
      targetElement.tagName === 'INPUT' ||
      targetElement.tagName === 'TEXTAREA' ||
      targetElement.tagName === 'BUTTON' ||
      targetElement.closest('.popover-trigger') ||
      targetElement.closest('input') ||
      targetElement.closest('textarea') ||
      targetElement.closest('button') ||
      // Check for specific interactive areas by looking for elements with onClick handlers
      targetElement.style.cursor === 'pointer' ||
      // Check parent elements for cursor pointer (category, task name, dates, progress spans)
      (targetElement.parentElement && targetElement.parentElement.style.cursor === 'pointer') ||
      // Check if this is the checkbox area
      targetElement.closest('[style*="borderRadius: 50%"]') ||
      // Check if clicking on expand/collapse icon
      targetElement.closest('svg') ||
      // Additional safety checks
      targetClasses.includes('progress') ||
      targetClasses.includes('predecessor') ||
      targetClasses.includes('successor') ||
      targetClasses.includes('department') ||
      targetClasses.includes('assigned-to') ||
      targetClasses.includes('actions');
    
    // Only open modal if NOT clicking on interactive elements
    if (!isInteractiveElement) {
      setShowTaskModal(true);
    }
  };

  // Handle modal close
  const handleCloseModal = () => {
    setShowTaskModal(false);
  };

  // Handle task update from modal
  const handleUpdateTaskFromModal = (updatedTask) => {
    setData((prevData) => {
      const newData = [...prevData];
      newData[index] = updatedTask;
      return newData;
    });
    setTaskData(updatedTask);
  };

  // Determine the color for progress bar
  const getProgressColor = (progress) => {
    const progressNum = parseInt(progress, 10);
    if (progressNum === 100) return "#22c55e"; // green-500
    if (progressNum < 50) return "#f43f5e"; // rose-500
    return "#fb923c"; // orange-400
  };

  // Format the initials for avatar
  const getInitials = (name) => {
    if (!name || typeof name !== "string") return "?";
    return name.charAt(0).toUpperCase();
  };

  // Toggle subtasks visibility
  const toggleSubtasks = () => {
    if (showSubtasks) {
      setShowSubtasks(false);
      setExpandedIndex(null);
    } else {
      setShowSubtasks(true);
      setExpandedIndex(index);
    }
  };

  // Toggle popover menu
  const togglePopover = (e) => {
    e.stopPropagation();
    setShowPopover(!showPopover);
    setPopoverSearch("");
  };

  // Toggle new popovers
  // const togglePredecessorPopover = (e, subIndex) => {
  //   e.stopPropagation();
  //   console.log("this is the sub index here...", subIndex);
  //   if (subIndex) {
  //     console.log("this is the sub index here...", subIndex);
  //   } else {
  //     setShowPredecessorPopover(!showPredecessorPopover);
  //     setPredecessorSearch("");
  //   }
  // };

  const togglePredecessorPopover = (e, subIndex = null) => {
    e.stopPropagation();
    setPredecessorPopovers((prev) => ({
      ...prev,
      [subIndex !== null ? `sub-${subIndex}` : "main"]:
        !prev[subIndex !== null ? `sub-${subIndex}` : "main"],
    }));
    setPredecessorSearch("");
  };

  // const toggleSuccessorPopover = (e) => {
  //   e.stopPropagation();
  //   setShowSuccessorPopover(!showSuccessorPopover);
  //   setSuccessorSearch("");
  // };

  const toggleSuccessorPopover = (e, subIndex = null) => {
    e.stopPropagation();
    setSuccessorPopovers((prev) => ({
      ...prev,
      [subIndex !== null ? `sub-${subIndex}` : "main"]:
        !prev[subIndex !== null ? `sub-${subIndex}` : "main"],
    }));
    setSuccessorSearch("");
  };

  // const toggleDepartmentPopover = (e) => {
  //   e.stopPropagation();
  //   setShowDepartmentPopover(!showDepartmentPopover);
  //   setDepartmentSearch("");
  // };

  const toggleDepartmentPopover = (e, subIndex = null) => {
    e.stopPropagation();
    setDepartmentPopovers((prev) => ({
      ...prev,
      [subIndex !== null ? `sub-${subIndex}` : "main"]:
        !prev[subIndex !== null ? `sub-${subIndex}` : "main"],
    }));
    setDepartmentSearch("");
  };

  // const toggleAssignedToPopover = (e) => {
  //   e.stopPropagation();
  //   setShowAssignedToPopover(!showAssignedToPopover);
  //   setAssignedToSearch("");
  // };

  // Toggle date picker for subtasks

  const toggleAssignedToPopover = (e, subIndex = null) => {
    e.stopPropagation();
    setAssignedToPopovers((prev) => ({
      ...prev,
      [subIndex !== null ? `sub-${subIndex}` : "main"]:
        !prev[subIndex !== null ? `sub-${subIndex}` : "main"],
    }));
    setAssignedToSearch("");
  };

  const toggleSubtaskDatePicker = (subIndex, field) => {
    setSubtaskDatePickers((prev) => ({
      ...prev,
      [`${subIndex}-${field}`]: !prev[`${subIndex}-${field}`],
    }));
  };

  const handleSubDragStart = (index) => {
    setDraggedSubIndex(index);
  };

  const handleSubDragEnter = (targetIndex) => {
    if (draggedSubIndex === null || draggedSubIndex === targetIndex) return;

    const updatedSubTasks = [...taskData.subTasks];
    const [draggedItem] = updatedSubTasks.splice(draggedSubIndex, 1);
    updatedSubTasks.splice(targetIndex, 0, draggedItem);

    // Optional: reassign currentIndex inside subtasks
    const remapped = updatedSubTasks.map((item, idx) => ({
      ...item,
      currentIndex: idx,
    }));

    // Update the parent task with reordered subtasks
    const updatedTask = {
      ...taskData,
      subTasks: remapped,
    };

    setDraggedSubIndex(targetIndex);
    setData((prev) => {
      const newData = [...prev];
      newData[index] = updatedTask; // Replace main task with updated subtask order
      return newData;
    });
  };

  const handleSubDragEnd = () => {
    setDraggedSubIndex(null);
  };

  const rowContainerStyle = {
    width: "100%",
    display: "table-row",
    borderBottom: showSubtasks ? "3px solid #fbbf24" : "1px solid #e5e7eb",
    backgroundColor:
      isDraggingOver === index
        ? "#fcecbd" // Drag highlight
        : showSubtasks
        ? "#fcecbd" // Subtask expanded background
        : index % 2 === 0
        ? "#f8fafc" // Even row color
        : "#f1f5f9", // Odd row color
    boxShadow: showSubtasks ? "0 2px 4px rgba(0,0,0,0.05)" : "none",
    transition: "background-color 0.2s ease-in-out",
    cursor: "move",
  };

  const subtaskRowStyle = {
    width: "100%",
    display: "table-row",
    borderBottom: "1px solid #e2e8f0",
    cursor: "move",
  };

  const cellStyle = {
    padding: "10px 16px",
    display: "table-cell",
    verticalAlign: "middle",
  };

  const wrappingCellStyle = {
    ...cellStyle,
    overflow: "hidden",
    textOverflow: "clip",
    whiteSpace: "normal",
    wordBreak: "break-word",
  };

  // Function to highlight matching text
  const highlightText = (text, query) => {
    if (!query || !text) return text;
    
    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <span key={index} style={{ 
          backgroundColor: '#fef3c7', 
          color: '#92400e',
          fontWeight: '600',
          padding: '1px 2px',
          borderRadius: '2px'
        }}>
          {part}
        </span>
      ) : part
    );
  };

  return (
    <>
      <div
        style={{
          borderWidth: 1,
          borderColor: "#d1d5db",
          borderRadius: 8,
          padding: 2,
          backgroundColor: "#ffffff",
          position: "relative",
        }}
      >
        {/* Drop Target Indicator */}
        {isDraggingOver && (
          <div
            style={{
              position: "absolute",
              top: "-2px",
              left: "0",
              right: "0",
              height: "4px",
              backgroundColor: "#3b82f6",
              borderRadius: "2px",
              zIndex: 10,
              boxShadow: "0 0 8px rgba(59, 130, 246, 0.6)",
              animation: "pulse 1.5s infinite",
            }}
          />
        )}
        
        {taskData && (
          <div
            style={rowContainerStyle}
            draggable
            onDragStart={onDragStart}
            onDragEnter={onDragEnter}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDragEnd={onDragEnd}
            onClick={handleTaskClick}
          >
            {/* Checkbox + Serial No */}
            <div style={{ ...cellStyle, width: "4%", position: "relative" }}>
              <div
                style={{
                  width: "20px",
                  height: "20px",
                  borderRadius: "50%",
                  border:
                    taskData.progress === "100"
                      ? "1px solid #20c314"
                      : "1px solid #94a3b8",
                  backgroundColor:
                    taskData.progress === "100" ? "#20c314" : "transparent",
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginRight: "8px",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleCheckboxChange();
                }}
              >
                {taskData.progress === "100" && (
                  <div
                    style={{
                      width: "8px",
                      height: "8px",
                      borderRadius: "50%",
                      backgroundColor: "#20c314",
                    }}
                  ></div>
                )}
              </div>
              <span style={{ fontWeight: "500" }}>
                {taskData.serialNumber || index + 1}
              </span>
            </div>

            {/* Category */}
            <div style={{ ...wrappingCellStyle, width: "8%" }}>
              {isEditingCategory ? (
                <textarea
                  ref={categoryInputRef}
                  value={categoryValue}
                  onChange={(e) => {
                    setCategoryValue(e.target.value);
                    adjustTextareaHeight(categoryInputRef.current);
                  }}
                  onBlur={() => {
                    setIsEditingCategory(false);
                    updateTaskData();
                  }}
                  onFocus={(e) => {
                    // Auto-select all text when focusing, especially useful for "Tap to enter"
                    e.target.select();
                  }}
                  style={{
                    width: "100%",
                    padding: "4px 8px",
                    border: "1px solid #d1d5db",
                    borderRadius: "4px",
                    fontSize: "14px",
                    outline: "none",
                    transition: "all 0.2s ease-in-out",
                    resize: "none",
                    minHeight: "40px",
                    lineHeight: "1.5",
                  }}
                />
              ) : (
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsEditingCategory(true);
                    // Auto-clear "Tap to enter" text
                    if (categoryValue === "Tap to enter") {
                      setCategoryValue("");
                    }
                  }}
                  style={{
                    cursor: "pointer",
                    transition: "background-color 0.2s ease-in-out",
                    padding: "4px 8px",
                    borderRadius: "4px",
                    display: "inline-block",
                  }}
                  onMouseOver={(e) =>
                    (e.currentTarget.style.backgroundColor = "#e2e8f0")
                  }
                  onMouseOut={(e) =>
                    (e.currentTarget.style.backgroundColor = "transparent")
                  }
                >
                  {highlightText(taskData.category, searchQuery)}
                </span>
              )}
            </div>

            {/* Task Name with expand/collapse button */}
            <div style={{ ...wrappingCellStyle, width: "16%" }}>
              <div style={{ display: "flex", alignItems: "center" }}>
                {taskData.subTasks && taskData.subTasks.length > 0 ? (
                  <button
                    style={{
                      marginRight: "8px",
                      color: "#475569",
                      border: "none",
                      background: "none",
                      cursor: "pointer",
                      padding: "0",
                      display: "flex",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleSubtasks();
                    }}
                  >
                    {showSubtasks ? (
                      <IoChevronUpCircleOutline size={16} />
                    ) : (
                      <IoChevronDownCircleOutline size={16} />
                    )}
                  </button>
                ) : (
                  <div style={{ width: "24px" }}></div>
                )}
                {isEditingTaskName ? (
                  <textarea
                    ref={taskNameInputRef}
                    value={taskNameValue}
                    onChange={(e) => {
                      setTaskNameValue(e.target.value);
                      adjustTextareaHeight(taskNameInputRef.current);
                    }}
                    onBlur={() => {
                      setIsEditingTaskName(false);
                      updateTaskData();
                    }}
                    onFocus={(e) => {
                      // Auto-select all text when focusing, especially useful for "Tap to enter"
                      e.target.select();
                    }}
                    style={{
                      width: "100%",
                      padding: "4px 8px",
                      border: "1px solid #d1d5db",
                      borderRadius: "4px",
                      fontSize: "14px",
                      outline: "none",
                      transition: "all 0.2s ease-in-out",
                      resize: "none",
                      minHeight: "40px",
                      lineHeight: "1.5",
                    }}
                  />
                ) : (
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsEditingTaskName(true);
                      // Auto-clear "Tap to enter" text
                      if (taskNameValue === "Tap to enter") {
                        setTaskNameValue("");
                      }
                    }}
                    style={{
                      cursor: "pointer",
                      transition: "background-color 0.2s ease-in-out",
                      padding: "4px 8px",
                      borderRadius: "4px",
                      display: "inline-block",
                    }}
                    onMouseOver={(e) =>
                      (e.currentTarget.style.backgroundColor = "#e2e8f0")
                    }
                    onMouseOut={(e) =>
                      (e.currentTarget.style.backgroundColor = "transparent")
                    }
                  >
                    {highlightText(taskData.taskName, searchQuery)}
                  </span>
                )}
              </div>
            </div>

            {/* Start Date */}
            <div style={{ ...cellStyle, width: "9%" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  color:
                    parseInt(taskData.progress) !== 100 ? "#f43f5e" : "#22c55e",
                }}
              >
                <div
                  style={{
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    backgroundColor:
                      parseInt(taskData.progress) !== 100 ? "#f43f5e" : "#22c55e",
                    marginRight: "8px",
                    display: "inline-block",
                  }}
                ></div>
                {showStartDatePicker ? (
                  <input
                    ref={startDateInputRef}
                    type="date"
                    value={taskData.startDate || ""}
                    onChange={(e) => {
                      updateTaskDate("startDate", e.target.value);
                      setShowStartDatePicker(false);
                    }}
                    onBlur={() => setShowStartDatePicker(false)}
                    style={{
                      padding: "4px",
                      border: "1px solid #d1d5db",
                      borderRadius: "4px",
                      fontSize: "14px",
                    }}
                  />
                ) : (
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowStartDatePicker(true);
                    }}
                    style={{ cursor: "pointer" }}
                  >
                    {taskData.startDate || "Yet to Start/-"}
                  </span>
                )}
              </div>
            </div>

            {/* End Date */}
            <div style={{ ...cellStyle, width: "9%" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  color:
                    parseInt(taskData.progress) !== 100 ? "#f43f5e" : "#22c55e",
                }}
              >
                <div
                  style={{
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    backgroundColor:
                      parseInt(taskData.progress) !== 100 ? "#f43f5e" : "#22c55e",
                    marginRight: "8px",
                    display: "inline-block",
                  }}
                ></div>
                {showEndDatePicker ? (
                  <input
                    ref={endDateInputRef}
                    type="date"
                    value={taskData.endDate || ""}
                    onChange={(e) => {
                      updateTaskDate("endDate", e.target.value);
                      setShowEndDatePicker(false);
                    }}
                    onBlur={() => setShowEndDatePicker(false)}
                    style={{
                      padding: "4px",
                      border: "1px solid #d1d5db",
                      borderRadius: "4px",
                      fontSize: "14px",
                    }}
                  />
                ) : (
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowEndDatePicker(true);
                    }}
                    style={{ cursor: "pointer" }}
                  >
                    {taskData.endDate || "------------"}
                  </span>
                )}
              </div>
            </div>

            {/* Duration */}
            <div style={{ ...cellStyle, width: "7%" }}>
              {calculateDuration(taskData.startDate, taskData.endDate)}
            </div>

            {/* Progress Bar */}
            <div style={{ ...cellStyle, width: "9%" }}>
              <div style={{ width: "100%" }}>
                {parseInt(taskData.progress) === 100 ? (
                  <div
                    style={{
                      fontSize: "12px",
                      fontWeight: "500",
                      color: "#ffffff",
                      backgroundColor: "#22c55e",
                      padding: "4px 8px",
                      borderRadius: "4px",
                      textAlign: "center",
                    }}
                  >
                    Completed 100%
                  </div>
                ) : (
                  <>
                    {isEditingProgress ? (
                      <input
                        ref={progressInputRef}
                        type="number"
                        min="0"
                        max="100"
                        value={progressValue}
                        onChange={(e) => setProgressValue(e.target.value)}
                        onBlur={() => {
                          setIsEditingProgress(false);
                          updateTaskData();
                        }}
                        style={{
                          width: "60px",
                          padding: "4px 8px",
                          border: "1px solid #d1d5db",
                          borderRadius: "4px",
                          fontSize: "14px",
                          outline: "none",
                          transition: "all 0.2s ease-in-out",
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          fontSize: "12px",
                          fontWeight: "500",
                          color: "#1e293b",
                          cursor: "pointer",
                          padding: "4px 8px",
                          borderRadius: "4px",
                          display: "inline-block",
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsEditingProgress(true);
                        }}
                        onMouseOver={(e) =>
                          (e.currentTarget.style.backgroundColor = "#e2e8f0")
                        }
                        onMouseOut={(e) =>
                          (e.currentTarget.style.backgroundColor = "transparent")
                        }
                      >
                        {`${taskData.progress}%`}
                      </div>
                    )}
                    <div
                      style={{
                        width: "100%",
                        backgroundColor: "#e2e8f0",
                        borderRadius: "9999px",
                        height: "8px",
                        marginTop: "4px",
                      }}
                    >
                      <div
                        style={{
                          height: "8px",
                          borderRadius: "9999px",
                          backgroundColor: getProgressColor(taskData.progress),
                          width: `${taskData.progress}%`,
                        }}
                      ></div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Predecessor */}
            <div style={{ ...cellStyle, width: "9%", position: "relative" }}>
              <span
                style={{
                  color:
                    taskData.startDate === "Yet to Start/-"
                      ? "#f43f5e"
                      : "#1e293b",
                  cursor: "pointer",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  togglePredecessorPopover(e);
                }}
              >
                {taskData.predecessor && taskData.predecessor.length > 0
                  ? taskData.predecessor.join(", ")
                  : "Task-1"}
              </span>
              {predecessorPopovers['main'] && (
                <div
                  // ref={predecessorPopoverRef}
                  ref={(el) => (popoverRefs.current.predecessor['main'] = el)}
                  style={{
                    position: "absolute",
                    top: "-100%",
                    left: 0,
                    backgroundColor: "#ffffff",
                    boxShadow:
                      "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                    borderRadius: "6px",
                    border: "1px solid #e2e8f0",
                    width: "160px",
                    zIndex: "100",
                  }}
                >
                  <input
                    type="text"
                    value={predecessorSearch}
                    onChange={(e) => setPredecessorSearch(e.target.value)}
                    placeholder="Search..."
                    style={{
                      width: "100%",
                      padding: "8px 16px",
                      borderBottom: "1px solid #e2e8f0",
                      border: "none",
                      fontSize: "14px",
                      outline: "none",
                    }}
                  />
                  <ul
                    style={{ listStyle: "none", margin: "0", padding: "4px 0" }}
                  >
                    {predecessorOptions
                      .filter((option) =>
                        option
                          .toLowerCase()
                          .includes(predecessorSearch.toLowerCase())
                      )
                      .map((option) => (
                        <li
                          key={option}
                          style={{
                            padding: "8px 16px",
                            fontSize: "14px",
                            cursor: "pointer",
                            color: "#1e293b",
                          }}
                          onMouseOver={(e) => {
                            e.currentTarget.style.backgroundColor = "#f1f5f9";
                          }}
                          onMouseOut={(e) => {
                            e.currentTarget.style.backgroundColor = "transparent";
                          }}
                          onClick={() => {
                            updateTaskField("predecessor", option);
                            // setShowPredecessorPopover(false);
                            setPredecessorPopovers((prev) => ({ ...prev, 'main': false }));
                            setPredecessorSearch("");
                          }}
                        >
                          {option}
                        </li>
                      ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Successor */}
            <div style={{ ...cellStyle, width: "8%", position: "relative" }}>
              <span
                style={{
                  color:
                    taskData.startDate === "Yet to Start/-"
                      ? "#f43f5e"
                      : "#1e293b",
                  cursor: "pointer",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleSuccessorPopover(e);
                }}
              >
                {taskData.successor && taskData.successor.length > 0
                  ? taskData.successor.join(", ")
                  : "Task-1"}
              </span>
              {successorPopovers['main'] && (
                <div
                  // ref={successorPopoverRef}
                  ref={(el) => (popoverRefs.current.successor['main'] = el)}
                  style={{
                    position: "absolute",
                    top: "-100%",
                    left: 0,
                    backgroundColor: "#ffffff",
                    boxShadow:
                      "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                    borderRadius: "6px",
                    border: "1px solid #e2e8f0",
                    width: "160px",
                    zIndex: "100",
                  }}
                >
                  <input
                    type="text"
                    value={successorSearch}
                    onChange={(e) => setSuccessorSearch(e.target.value)}
                    placeholder="Search..."
                    style={{
                      width: "100%",
                      padding: "8px 16px",
                      borderBottom: "1px solid #e2e8f0",
                      border: "none",
                      fontSize: "14px",
                      outline: "none",
                    }}
                  />
                  <ul
                    style={{ listStyle: "none", margin: "0", padding: "4px 0" }}
                  >
                    {successorOptions
                      .filter((option) =>
                        option
                          .toLowerCase()
                          .includes(successorSearch.toLowerCase())
                      )
                      .map((option) => (
                        <li
                          key={option}
                          style={{
                            padding: "8px 16px",
                            fontSize: "14px",
                            cursor: "pointer",
                            color: "#1e293b",
                          }}
                          onMouseOver={(e) => {
                            e.currentTarget.style.backgroundColor = "#f1f5f9";
                          }}
                          onMouseOut={(e) => {
                            e.currentTarget.style.backgroundColor = "transparent";
                          }}
                          onClick={() => {
                            updateTaskField("successor", option);
                            // setShowSuccessorPopover(false);
                            setSuccessorPopovers((prev) => ({ ...prev, 'main': false }));
                            setSuccessorSearch("");
                          }}
                        >
                          {option}
                        </li>
                      ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Department */}
            <div style={{ ...cellStyle, width: "8%", position: "relative" }}>
              <span
                style={{ cursor: "pointer" }}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleDepartmentPopover(e);
                }}
              >
                {taskData.department || "Onsite"}
              </span>
              {departmentPopovers['main'] && (
                <div
                  // ref={departmentPopoverRef}
                  ref={(el) => (popoverRefs.current.department['main'] = el)}
                  style={{
                    position: "absolute",
                    top: "-100%",
                    left: 0,
                    backgroundColor: "#ffffff",
                    boxShadow:
                      "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                    borderRadius: "6px",
                    border: "1px solid #e2e8f0",
                    width: "160px",
                    zIndex: "100",
                  }}
                >
                  <input
                    type="text"
                    value={departmentSearch}
                    onChange={(e) => setDepartmentSearch(e.target.value)}
                    placeholder="Search..."
                    style={{
                      width: "100%",
                      padding: "8px 16px",
                      borderBottom: "1px solid #e2e8f0",
                      border: "none",
                      fontSize: "14px",
                      outline: "none",
                    }}
                  />
                  <ul
                    style={{ listStyle: "none", margin: "0", padding: "4px 0" }}
                  >
                    {departmentOptions
                      .filter((option) =>
                        option
                          .toLowerCase()
                          .includes(departmentSearch.toLowerCase())
                      )
                      .map((option) => (
                        <li
                          key={option}
                          style={{
                            padding: "8px 16px",
                            fontSize: "14px",
                            cursor: "pointer",
                            color: "#1e293b",
                          }}
                          onMouseOver={(e) => {
                            e.currentTarget.style.backgroundColor = "#f1f5f9";
                          }}
                          onMouseOut={(e) => {
                            e.currentTarget.style.backgroundColor = "transparent";
                          }}
                          onClick={() => {
                            updateTaskField("department", option);
                            // setShowDepartmentPopover(false);
                            setDepartmentPopovers((prev) => ({ ...prev, 'main': false }));
                            setDepartmentSearch("");
                          }}
                        >
                          {option}
                        </li>
                      ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Assigned To */}
            <div style={{ ...cellStyle, width: "8%", position: "relative" }}>
              <div style={{ display: "flex", alignItems: "center" }}>
                <span
                  style={{ marginRight: "8px", cursor: "pointer" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleAssignedToPopover(e);
                  }}
                >
                  {taskData.assignedTo && taskData.assignedTo.length > 0
                    ? taskData.assignedTo.join(", ")
                    : "Karthik"}
                </span>
                <div
                  style={{
                    width: "28px",
                    height: "28px",
                    borderRadius: "50%",
                    backgroundColor: "#f59e0b",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#ffffff",
                    fontWeight: "600",
                    fontSize: "12px",
                    border: "2px solid #ffffff",
                    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                    flexShrink: 0,
                  }}
                >
                  {getInitials(
                    taskData.assignedTo && taskData.assignedTo.length > 0
                      ? taskData.assignedTo[0]
                      : "Karthik"
                  )}
                </div>
              </div>
              {assignedToPopovers['main'] && (
                <div
                  // ref={assignedToPopoverRef}
                  ref={(el) => (popoverRefs.current.assignedTo['main'] = el)}
                  style={{
                    position: "absolute",
                    top: "-100%",
                    left: 0,
                    backgroundColor: "#ffffff",
                    boxShadow:
                      "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                    borderRadius: "6px",
                    border: "1px solid #e2e8f0",
                    width: "160px",
                    zIndex: "100",
                  }}
                >
                  <input
                    type="text"
                    value={assignedToSearch}
                    onChange={(e) => setAssignedToSearch(e.target.value)}
                    placeholder="Search..."
                    style={{
                      width: "100%",
                      padding: "8px 16px",
                      borderBottom: "1px solid #e2e8f0",
                      border: "none",
                      fontSize: "14px",
                      outline: "none",
                    }}
                  />
                  <ul
                    style={{ listStyle: "none", margin: "0", padding: "4px 0" }}
                  >
                    {assignedToOptions
                      .filter((option) =>
                        option
                          .toLowerCase()
                          .includes(assignedToSearch.toLowerCase())
                      )
                      .map((option) => (
                        <li
                          key={option}
                          style={{
                            padding: "8px 16px",
                            fontSize: "14px",
                            cursor: "pointer",
                            color: "#1e293b",
                          }}
                          onMouseOver={(e) => {
                            e.currentTarget.style.backgroundColor = "#f1f5f9";
                          }}
                          onMouseOut={(e) => {
                            e.currentTarget.style.backgroundColor = "transparent";
                          }}
                          onClick={() => {
                            updateTaskField("assignedTo", option);
                            setAssignedToPopovers((prev) => ({ ...prev, 'main': false }));
                            // setShowAssignedToPopover(false);
                            setAssignedToSearch("");
                          }}
                        >
                          {option}
                        </li>
                      ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Estimated Delay */}
            <div style={{ ...cellStyle, width: "10%" }}>
              {taskData.estimatedDelay || "2 days"}
            </div>

            {/* Actions */}
            <div style={{ ...cellStyle, width: "3%", position: "relative" }}>
              <button
                className="popover-trigger"
                style={{
                  color: "#475569",
                  border: "none",
                  background: "none",
                  cursor: "pointer",
                  padding: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                onClick={togglePopover}
              >
                <FiMoreVertical size={18} />
              </button>

              {showPopover && (
                <div
                  ref={popoverRef}
                  style={{
                    position: "absolute",
                    top: "-100%",
                    right: 0,
                    backgroundColor: "#ffffff",
                    boxShadow:
                      "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                    borderRadius: "6px",
                    border: "1px solid #e2e8f0",
                    width: "160px",
                    zIndex: "100",
                  }}
                >
                  <input
                    type="text"
                    value={popoverSearch}
                    onChange={(e) => setPopoverSearch(e.target.value)}
                    placeholder="Search..."
                    style={{
                      width: "100%",
                      padding: "8px 16px",
                      borderBottom: "1px solid #e2e8f0",
                      border: "none",
                      fontSize: "14px",
                      outline: "none",
                    }}
                  />
                  <ul
                    style={{ listStyle: "none", margin: "0", padding: "4px 0" }}
                  >
                    {actionOptions
                      .filter((option) =>
                        option.toLowerCase().includes(popoverSearch.toLowerCase())
                      )
                      .map((option, idx) => (
                        <li
                          key={option}
                          style={{
                            padding: "8px 16px",
                            fontSize: "14px",
                            cursor: "pointer",
                            color: "#1e293b",
                            borderTop:
                              option === "Add Subtask" && idx > 0
                                ? "1px solid #e2e8f0"
                                : "none",
                          }}
                          onMouseOver={(e) => {
                            e.currentTarget.style.backgroundColor = "#f1f5f9";
                          }}
                          onMouseOut={(e) => {
                            e.currentTarget.style.backgroundColor = "transparent";
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAction(option);
                          }}
                        >
                          {option}
                        </li>
                      ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Subtasks */}
        {taskData &&
          showSubtasks &&
          taskData.subTasks &&
          taskData.subTasks.length > 0 && (
            <>
              {taskData.subTasks.map((subTask, subIndex) => (
                <div
                  key={`subtask-${subIndex}`}
                  style={{
                    ...subtaskRowStyle,
                    backgroundColor:
                      draggedSubIndex === subIndex ? "#fbeec3" : "#f9f4e4",
                  }}
                  draggable
                  onDragStart={() => handleSubDragStart(subIndex)}
                  onDragEnter={() => handleSubDragEnter(subIndex)}
                  onDragEnd={handleSubDragEnd}
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Checkbox + Serial No */}
                  <div
                    style={{
                      ...cellStyle,
                      width: "4%",
                      position: "relative",
                      paddingLeft: "35px",
                    }}
                  >
                    <div
                      style={{
                        width: "20px",
                        height: "20px",
                        borderRadius: "50%",
                        border:
                          subTask.progress === "100"
                            ? "1px solid #20c314"
                            : "1px solid #94a3b8",
                        backgroundColor:
                          subTask.progress === "100" ? "#20c314" : "transparent",
                        cursor: "pointer",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginRight: "8px",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCheckboxChange(true, subIndex);
                      }}
                    >
                      {subTask.progress === "100" && (
                        <div
                          style={{
                            width: "8px",
                            height: "8px",
                            borderRadius: "50%",
                            backgroundColor: "#20c314",
                          }}
                        ></div>
                      )}
                    </div>
                    <span style={{ fontWeight: "500" }}>
                      {subTask.serialNumber || `${index + 1}.${subIndex + 1}`}
                    </span>
                  </div>

                  {/* Category */}
                  <div style={{ ...wrappingCellStyle, width: "8%" }}>
                    {editingSubtaskFields[`${subIndex}-category`] ? (
                      <textarea
                        ref={(el) =>
                          (subtaskInputRefs.current[`${subIndex}-category`] = el)
                        }
                        value={subtaskValues[subIndex]?.category || ""}
                        onChange={(e) => {
                          const newValues = [...subtaskValues];
                          newValues[subIndex] = {
                            ...newValues[subIndex],
                            category: e.target.value,
                          };
                          setSubtaskValues(newValues);
                          adjustTextareaHeight(
                            subtaskInputRefs.current[`${subIndex}-category`]
                          );
                        }}
                        onBlur={() => {
                          setEditingSubtaskFields((prev) => ({
                            ...prev,
                            [`${subIndex}-category`]: false,
                          }));
                          updateSubtaskData(subIndex, "category");
                        }}
                        style={{
                          width: "100%",
                          padding: "4px 8px",
                          border: "1px solid #d1d5db",
                          borderRadius: "4px",
                          fontSize: "14px",
                          outline: "none",
                          transition: "all 0.2s ease-in-out",
                          resize: "none",
                          minHeight: "40px",
                          lineHeight: "1.5",
                        }}
                      />
                    ) : (
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingSubtaskFields((prev) => ({
                            ...prev,
                            [`${subIndex}-category`]: true,
                          }));
                          // Auto-clear "Tap to enter" text
                          if (subtaskValues[subIndex]?.category === "Tap to enter") {
                            const newValues = [...subtaskValues];
                            newValues[subIndex] = {
                              ...newValues[subIndex],
                              category: "",
                            };
                            setSubtaskValues(newValues);
                          }
                        }}
                        style={{
                          cursor: "pointer",
                          transition: "background-color 0.2s ease-in-out",
                          padding: "4px 8px",
                          borderRadius: "4px",
                          display: "inline-block",
                        }}
                        onMouseOver={(e) =>
                          (e.currentTarget.style.backgroundColor = "#e2e8f0")
                        }
                        onMouseOut={(e) =>
                          (e.currentTarget.style.backgroundColor = "transparent")
                        }
                      >
                        {highlightText(subTask.category, searchQuery)}
                      </span>
                    )}
                  </div>

                  {/* Task Name */}
                  <div
                    style={{
                      ...wrappingCellStyle,
                      width: "16%",
                      paddingLeft: "35px",
                    }}
                  >
                    {editingSubtaskFields[`${subIndex}-taskName`] ? (
                      <textarea
                        ref={(el) =>
                          (subtaskInputRefs.current[`${subIndex}-taskName`] = el)
                        }
                        value={subtaskValues[subIndex]?.taskName || ""}
                        onChange={(e) => {
                          const newValues = [...subtaskValues];
                          newValues[subIndex] = {
                            ...newValues[subIndex],
                            taskName: e.target.value,
                          };
                          setSubtaskValues(newValues);
                          adjustTextareaHeight(
                            subtaskInputRefs.current[`${subIndex}-taskName`]
                          );
                        }}
                        onBlur={() => {
                          setEditingSubtaskFields((prev) => ({
                            ...prev,
                            [`${subIndex}-taskName`]: false,
                          }));
                          updateSubtaskData(subIndex, "taskName");
                        }}
                        style={{
                          width: "100%",
                          padding: "4px 8px",
                          border: "1px solid #d1d5db",
                          borderRadius: "4px",
                          fontSize: "14px",
                          outline: "none",
                          transition: "all 0.2s ease-in-out",
                          resize: "none",
                          minHeight: "40px",
                          lineHeight: "1.5",
                        }}
                      />
                    ) : (
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingSubtaskFields((prev) => ({
                            ...prev,
                            [`${subIndex}-taskName`]: true,
                          }));
                          // Auto-clear "Tap to enter" text
                          if (subtaskValues[subIndex]?.taskName === "Tap to enter") {
                            const newValues = [...subtaskValues];
                            newValues[subIndex] = {
                              ...newValues[subIndex],
                              taskName: "",
                            };
                            setSubtaskValues(newValues);
                          }
                        }}
                        style={{
                          cursor: "pointer",
                          transition: "background-color 0.2s ease-in-out",
                          padding: "4px 8px",
                          borderRadius: "4px",
                          display: "inline-block",
                        }}
                        onMouseOver={(e) =>
                          (e.currentTarget.style.backgroundColor = "#e2e8f0")
                        }
                        onMouseOut={(e) =>
                          (e.currentTarget.style.backgroundColor = "transparent")
                        }
                      >
                        {highlightText(subTask.taskName, searchQuery)}
                      </span>
                    )}
                  </div>

                  {/* Start Date */}
                  <div style={{ ...cellStyle, width: "9%" }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        color:
                          parseInt(subTask.progress) !== 100
                            ? "#f43f5e"
                            : "#22c55e",
                      }}
                    >
                      <div
                        style={{
                          width: "8px",
                          height: "8px",
                          borderRadius: "50%",
                          backgroundColor:
                            parseInt(subTask.progress) !== 100
                              ? "#f43f5e"
                              : "#22c55e",
                          marginRight: "8px",
                          display: "inline-block",
                        }}
                      ></div>
                      {subtaskDatePickers[`${subIndex}-startDate`] ? (
                        <input
                          ref={(el) =>
                            (subtaskDateInputRefs.current[
                              `${subIndex}-startDate`
                            ] = el)
                          }
                          type="date"
                          value={subTask.startDate || ""}
                          onChange={(e) => {
                            updateSubtaskDate(
                              subIndex,
                              "startDate",
                              e.target.value
                            );
                            toggleSubtaskDatePicker(subIndex, "startDate");
                          }}
                          onBlur={() =>
                            toggleSubtaskDatePicker(subIndex, "startDate")
                          }
                          style={{
                            padding: "4px",
                            border: "1px solid #d1d5db",
                            borderRadius: "4px",
                            fontSize: "14px",
                          }}
                        />
                      ) : (
                        <span
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleSubtaskDatePicker(subIndex, "startDate");
                          }}
                          style={{ cursor: "pointer" }}
                        >
                          {subTask.startDate || "Yet to Start/-"}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* End Date */}
                  <div style={{ ...cellStyle, width: "9%" }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        color:
                          parseInt(subTask.progress) !== 100
                            ? "#f43f5e"
                            : "#22c55e",
                      }}
                    >
                      <div
                        style={{
                          width: "8px",
                          height: "8px",
                          borderRadius: "50%",
                          backgroundColor:
                            parseInt(subTask.progress) !== 100
                              ? "#f43f5e"
                              : "#22c55e",
                          marginRight: "8px",
                          display: "inline-block",
                        }}
                      ></div>
                      {subtaskDatePickers[`${subIndex}-endDate`] ? (
                        <input
                          ref={(el) =>
                            (subtaskDateInputRefs.current[`${subIndex}-endDate`] =
                              el)
                          }
                          type="date"
                          value={subTask.endDate || ""}
                          onChange={(e) => {
                            updateSubtaskDate(
                              subIndex,
                              "endDate",
                              e.target.value
                            );
                            toggleSubtaskDatePicker(subIndex, "endDate");
                          }}
                          onBlur={() =>
                            toggleSubtaskDatePicker(subIndex, "endDate")
                          }
                          style={{
                            padding: "4px",
                            border: "1px solid #d1d5db",
                            borderRadius: "4px",
                            fontSize: "14px",
                          }}
                        />
                      ) : (
                        <span
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleSubtaskDatePicker(subIndex, "endDate");
                          }}
                          style={{ cursor: "pointer" }}
                        >
                          {subTask.endDate || "------------"}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Duration */}
                  <div style={{ ...cellStyle, width: "7%" }}>
                    {calculateDuration(subTask.startDate, subTask.endDate)}
                  </div>

                  {/* Progress Bar */}
                  <div style={{ ...cellStyle, width: "9%" }}>
                    <div style={{ width: "100%" }}>
                      {parseInt(subTask.progress) === 100 ? (
                        <div
                          style={{
                            fontSize: "12px",
                            fontWeight: "500",
                            color: "#ffffff",
                            backgroundColor: "#22c55e",
                            padding: "4px 8px",
                            borderRadius: "4px",
                            textAlign: "center",
                          }}
                        >
                          Completed 100%
                        </div>
                      ) : (
                        <>
                          {editingSubtaskFields[`${subIndex}-progress`] ? (
                            <input
                              ref={(el) =>
                                (subtaskInputRefs.current[
                                  `${subIndex}-progress`
                                ] = el)
                              }
                              type="number"
                              min="0"
                              max="100"
                              value={subtaskValues[subIndex]?.progress || ""}
                              onChange={(e) => {
                                const newValues = [...subtaskValues];
                                newValues[subIndex] = {
                                  ...newValues[subIndex],
                                  progress: e.target.value,
                                };
                                setSubtaskValues(newValues);
                              }}
                              onBlur={() => {
                                setEditingSubtaskFields((prev) => ({
                                  ...prev,
                                  [`${subIndex}-progress`]: false,
                                }));
                                updateSubtaskData(subIndex, "progress");
                              }}
                              style={{
                                width: "60px",
                                padding: "4px 8px",
                                border: "1px solid #d1d5db",
                                borderRadius: "4px",
                                fontSize: "14px",
                                outline: "none",
                                transition: "all 0.2s ease-in-out",
                              }}
                            />
                          ) : (
                            <div
                              style={{
                                fontSize: "12px",
                                fontWeight: "500",
                                color: "#1e293b",
                                cursor: "pointer",
                                padding: "4px 8px",
                                borderRadius: "4px",
                                display: "inline-block",
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingSubtaskFields((prev) => ({
                                  ...prev,
                                  [`${subIndex}-progress`]: true,
                                }));
                              }}
                              onMouseOver={(e) =>
                                (e.currentTarget.style.backgroundColor = "#e2e8f0")
                              }
                              onMouseOut={(e) =>
                                (e.currentTarget.style.backgroundColor = "transparent")
                              }
                            >
                              {`${subTask.progress}%`}
                            </div>
                          )}
                          <div
                            style={{
                              width: "100%",
                              backgroundColor: "#e2e8f0",
                              borderRadius: "9999px",
                              height: "8px",
                              marginTop: "4px",
                            }}
                          >
                            <div
                              style={{
                                height: "8px",
                                borderRadius: "9999px",
                                backgroundColor: getProgressColor(
                                  subTask.progress
                                ),
                                width: `${subTask.progress}%`,
                              }}
                            ></div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Predecessor */}
                  <div
                    style={{ ...cellStyle, width: "9%", position: "relative" }}
                  >
                    <span
                      style={{
                        color:
                          subTask.startDate === "Yet to Start/-"
                            ? "#f43f5e"
                            : "#1e293b",
                        cursor: "pointer",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        togglePredecessorPopover(e, subIndex);
                      }}
                    >
                      {subTask.predecessor && subTask.predecessor.length > 0
                        ? subTask.predecessor.join(", ")
                        : "Task-1"}
                    </span>
                    {predecessorPopovers[`sub-${subIndex}`] && (
                      <div
                        // ref={predecessorPopoverRef}
                        ref={(el) => (popoverRefs.current.predecessor[`sub-${subIndex}`] = el)}
                        style={{
                          position: "absolute",
                          top: "-100%",
                          left: 0,
                          backgroundColor: "#ffffff",
                          boxShadow:
                            "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                          borderRadius: "6px",
                          border: "1px solid #e2e8f0",
                          width: "160px",
                          zIndex: "100",
                        }}
                      >
                        <input
                          type="text"
                          value={predecessorSearch}
                          onChange={(e) => setPredecessorSearch(e.target.value)}
                          placeholder="Search..."
                          style={{
                            width: "100%",
                            padding: "8px 16px",
                            borderBottom: "1px solid #e2e8f0",
                            border: "none",
                            fontSize: "14px",
                            outline: "none",
                          }}
                        />
                        <ul
                          style={{
                            listStyle: "none",
                            margin: "0",
                            padding: "4px 0",
                          }}
                        >
                          {predecessorOptions
                            .filter((option) =>
                              option
                                .toLowerCase()
                                .includes(predecessorSearch.toLowerCase())
                            )
                            .map((option) => (
                              <li
                                key={option}
                                style={{
                                  padding: "8px 16px",
                                  fontSize: "14px",
                                  cursor: "pointer",
                                  color: "#1e293b",
                                }}
                                onMouseOver={(e) => {
                                  e.currentTarget.style.backgroundColor =
                                    "#f1f5f9";
                                }}
                                onMouseOut={(e) => {
                                  e.currentTarget.style.backgroundColor =
                                    "transparent";
                                }}
                                onClick={() => {
                                  updateSubtaskField(
                                    subIndex,
                                    "predecessor",
                                    option
                                  );
                                  // setShowPredecessorPopover(false);
                                  setPredecessorPopovers((prev) => ({ ...prev, [`sub-${subIndex}`]: false }));
                                  setPredecessorSearch("");
                                }}
                              >
                                {option}
                              </li>
                            ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Successor */}
                  <div
                    style={{ ...cellStyle, width: "8%", position: "relative" }}
                  >
                    <span
                      style={{
                        color:
                          subTask.startDate === "Yet to Start/-"
                            ? "#f43f5e"
                            : "#1e293b",
                        cursor: "pointer",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleSuccessorPopover(e, subIndex);
                      }}
                    >
                      {subTask.successor && subTask.successor.length > 0
                        ? subTask.successor.join(", ")
                        : "Task-1"}
                    </span>
                    {successorPopovers[`sub-${subIndex}`] && (
                      <div
                        // ref={successorPopoverRef}
                        ref={(el) => (popoverRefs.current.successor[`sub-${subIndex}`] = el)}
                        style={{
                          position: "absolute",
                          top: "-100%",
                          left: 0,
                          backgroundColor: "#ffffff",
                          boxShadow:
                            "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                          borderRadius: "6px",
                          border: "1px solid #e2e8f0",
                          width: "160px",
                          zIndex: "100",
                        }}
                      >
                        <input
                          type="text"
                          value={successorSearch}
                          onChange={(e) => setSuccessorSearch(e.target.value)}
                          placeholder="Search..."
                          style={{
                            width: "100%",
                            padding: "8px 16px",
                            borderBottom: "1px solid #e2e8f0",
                            border: "none",
                            fontSize: "14px",
                            outline: "none",
                          }}
                        />
                        <ul
                          style={{
                            listStyle: "none",
                            margin: "0",
                            padding: "4px 0",
                          }}
                        >
                          {successorOptions
                            .filter((option) =>
                              option
                                .toLowerCase()
                                .includes(successorSearch.toLowerCase())
                            )
                            .map((option) => (
                              <li
                                key={option}
                                style={{
                                  padding: "8px 16px",
                                  fontSize: "14px",
                                  cursor: "pointer",
                                  color: "#1e293b",
                                }}
                                onMouseOver={(e) => {
                                  e.currentTarget.style.backgroundColor =
                                    "#f1f5f9";
                                }}
                                onMouseOut={(e) => {
                                  e.currentTarget.style.backgroundColor =
                                    "transparent";
                                }}
                                onClick={() => {
                                  updateSubtaskField(
                                    subIndex,
                                    "successor",
                                    option
                                  );
                                  // setShowSuccessorPopover(false);
                                  setSuccessorPopovers((prev) => ({ ...prev, [`sub-${subIndex}`]: false }));
                                  setSuccessorSearch("");
                                }}
                              >
                                {option}
                              </li>
                            ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Department */}
                  <div
                    style={{ ...cellStyle, width: "8%", position: "relative" }}
                  >
                    <span
                      style={{ cursor: "pointer" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleDepartmentPopover(e, subIndex);
                      }}
                    >
                      {subTask.department || "Onsite"}
                    </span>
                    {departmentPopovers[`sub-${subIndex}`] && (
                      <div
                        // ref={departmentPopoverRef}
                        ref={(el) => (popoverRefs.current.department[`sub-${subIndex}`] = el)}
                        style={{
                          position: "absolute",
                          top: "-100%",
                          left: 0,
                          backgroundColor: "#ffffff",
                          boxShadow:
                            "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                          borderRadius: "6px",
                          border: "1px solid #e2e8f0",
                          width: "160px",
                          zIndex: "100",
                        }}
                      >
                        <input
                          type="text"
                          value={departmentSearch}
                          onChange={(e) => setDepartmentSearch(e.target.value)}
                          placeholder="Search..."
                          style={{
                            width: "100%",
                            padding: "8px 16px",
                            borderBottom: "1px solid #e2e8f0",
                            border: "none",
                            fontSize: "14px",
                            outline: "none",
                          }}
                        />
                        <ul
                          style={{
                            listStyle: "none",
                            margin: "0",
                            padding: "4px 0",
                          }}
                        >
                          {departmentOptions
                            .filter((option) =>
                              option
                                .toLowerCase()
                                .includes(departmentSearch.toLowerCase())
                            )
                            .map((option) => (
                              <li
                                key={option}
                                style={{
                                  padding: "8px 16px",
                                  fontSize: "14px",
                                  cursor: "pointer",
                                  color: "#1e293b",
                                }}
                                onMouseOver={(e) => {
                                  e.currentTarget.style.backgroundColor =
                                    "#f1f5f9";
                                }}
                                onMouseOut={(e) => {
                                  e.currentTarget.style.backgroundColor =
                                    "transparent";
                                }}
                                onClick={() => {
                                  updateSubtaskField(
                                    subIndex,
                                    "department",
                                    option
                                  );
                                  // setShowDepartmentPopover(false);
                                  setDepartmentPopovers((prev) => ({ ...prev, [`sub-${subIndex}`]: false }));
                                  setDepartmentSearch("");
                                }}
                              >
                                {option}
                              </li>
                            ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Assigned To */}
                  <div
                    style={{ ...cellStyle, width: "8%", position: "relative" }}
                  >
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <span
                        style={{ marginRight: "8px", cursor: "pointer" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleAssignedToPopover(e, subIndex);
                        }}
                      >
                        {subTask.assignedTo && subTask.assignedTo.length > 0
                          ? subTask.assignedTo.join(", ")
                          : "Karthik"}
                      </span>
                      <div
                        style={{
                          width: "28px",
                          height: "28px",
                          borderRadius: "50%",
                          backgroundColor: "#f59e0b",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#ffffff",
                          fontWeight: "600",
                          fontSize: "12px",
                          border: "2px solid #ffffff",
                          boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                          flexShrink: 0,
                        }}
                      >
                        {getInitials(
                          subTask.assignedTo && subTask.assignedTo.length > 0
                            ? subTask.assignedTo[0]
                            : "Karthik"
                        )}
                      </div>
                    </div>
                    {assignedToPopovers[`sub-${subIndex}`] && (
                      <div
                        // ref={assignedToPopoverRef}
                        ref={(el) => (popoverRefs.current.assignedTo[`sub-${subIndex}`] = el)}
                        style={{
                          position: "absolute",
                          top: "-100%",
                          left: 0,
                          backgroundColor: "#ffffff",
                          boxShadow:
                            "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                          borderRadius: "6px",
                          border: "1px solid #e2e8f0",
                          width: "160px",
                          zIndex: "100",
                        }}
                      >
                        <input
                          type="text"
                          value={assignedToSearch}
                          onChange={(e) => setAssignedToSearch(e.target.value)}
                          placeholder="Search..."
                          style={{
                            width: "100%",
                            padding: "8px 16px",
                            borderBottom: "1px solid #e2e8f0",
                            border: "none",
                            fontSize: "14px",
                            outline: "none",
                          }}
                        />
                        <ul
                          style={{
                            listStyle: "none",
                            margin: "0",
                            padding: "4px 0",
                          }}
                        >
                          {assignedToOptions
                            .filter((option) =>
                              option
                                .toLowerCase()
                                .includes(assignedToSearch.toLowerCase())
                            )
                            .map((option) => (
                              <li
                                key={option}
                                style={{
                                  padding: "8px 16px",
                                  fontSize: "14px",
                                  cursor: "pointer",
                                  color: "#1e293b",
                                }}
                                onMouseOver={(e) => {
                                  e.currentTarget.style.backgroundColor =
                                    "#f1f5f9";
                                }}
                                onMouseOut={(e) => {
                                  e.currentTarget.style.backgroundColor =
                                    "transparent";
                                }}
                                onClick={() => {
                                  updateSubtaskField(
                                    subIndex,
                                    "assignedTo",
                                    option
                                  );
                                  // setShowAssignedToPopover(false);
                                  setAssignedToPopovers((prev) => ({ ...prev, [`sub-${subIndex}`]: false }));
                                  setAssignedToSearch("");
                                }}
                              >
                                {option}
                              </li>
                            ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Estimated Delay */}
                  <div style={{ ...cellStyle, width: "10%" }}>
                    {subTask.estimatedDelay || "2 days"}
                  </div>

                  {/* Remove subtask button */}
                  <div style={{ ...cellStyle, width: "3%" }}>
                    <button
                      style={{
                        color: "#475569",
                        border: "none",
                        background: "none",
                        cursor: "pointer",
                        padding: "0",
                      }}
                      onClick={() => removeSubtask(subIndex)}
                    >
                      <RxCross2 size={18} />
                    </button>
                  </div>
                </div>
              ))}
            </>
          )}
      </div>

      {/* Task Detail Modal */}
      <TaskDetailModal
        isOpen={showTaskModal}
        onClose={handleCloseModal}
        task={taskData}
        onUpdateTask={handleUpdateTaskFromModal}
      />
    </>
  );
}
